import { useState } from "react";
import {
  Drawer, Box, Typography, Button, Table, TableHead, TableBody, TableRow, TableCell,
  CircularProgress, Alert, Stack, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import UndoIcon from "@mui/icons-material/Undo";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { useBinderVerificationJob } from "../../hooks/useBinderVerificationJob";
import {
  useTriggerBinderVerification,
  useSetMissingDocVerdict,
  useClearMissingDocVerdict,
} from "@taxassist/api-client";

interface Props {
  firmId: string | null;
  jobId: string | null;
  onClose: () => void;
}

interface ReviewerVerdict {
  verdict?: "verified_present" | "not_required";
  by?: string;
  byEmail?: string;
  at?: string;
  note?: string;
}

interface MissingDoc {
  fileName: string;
  s3Key: string;
  documentType?: string;
  issuer?: string;
  accountNumber?: string;
  reviewerVerdict?: ReviewerVerdict;
}

interface BinderArtifact {
  artifactId?: string;
  artifactType?: string;
  fileName?: string;
  verification?: {
    status?: string;
    pipelineStatus?: string;
    partiallyScanned?: boolean;
    missingDocs?: MissingDoc[];
  };
}

interface JobLike {
  clientName?: string;
  processingResults?: BinderArtifact[];
}

function pickBinder(job: JobLike | undefined): BinderArtifact | undefined {
  return (job?.processingResults ?? []).find(
    (r) =>
      r.artifactType === "binder" ||
      (r.artifactType === "supporting_doc" && /\b(bookmark|binder)/i.test(r.fileName ?? "")),
  );
}

// base64url-encode the S3 key for the URL path. Browser-safe: we
// deliberately avoid Node's Buffer (not a browser global) — it threw a
// ReferenceError on click in prod, silently swallowed by the bulk
// loop's try/catch and the per-row try/finally, so the symptom was
// "Marking N/N feedback but no POST ever fires". Tests had run in Node
// where Buffer exists, so they missed it.
function encodeS3KeyForPath(s3Key: string): string {
  const bytes = new TextEncoder().encode(s3Key);
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function VerdictChip({ verdict }: { verdict: ReviewerVerdict }) {
  const label =
    verdict.verdict === "verified_present"
      ? "Marked present"
      : verdict.verdict === "not_required"
        ? "Not required"
        : "Resolved";
  const tip =
    `By ${verdict.byEmail ?? verdict.by ?? "?"}` +
    (verdict.at ? ` on ${new Date(verdict.at).toLocaleString()}` : "") +
    (verdict.note ? ` — ${verdict.note}` : "");
  return (
    <Tooltip title={tip}>
      <Chip
        size="small"
        color={verdict.verdict === "verified_present" ? "success" : "default"}
        label={label}
      />
    </Tooltip>
  );
}

export function BinderVerificationDrawer({ firmId, jobId, onClose }: Props) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useBinderVerificationJob(firmId, jobId);
  const trigger = useTriggerBinderVerification();
  const setVerdict = useSetMissingDocVerdict();
  const clearVerdict = useClearMissingDocVerdict();

  // After any verdict mutation we need to nudge the summary list behind
  // this drawer to refetch — otherwise the table row's chip stays stale
  // because the summary query has no polling once a binder is completed.
  //
  // The predicate intentionally checks "binder-verification" AND "summary"
  // as two independent substring matches across the queryKey array, rather
  // than looking for the full `binder-verification/summary` slash-joined
  // path. orval currently emits the full URL template as a single string
  // key, so either form would match today — but the segmented form also
  // matches if future keys are split into separate path segments (e.g.
  // `["binder-verification", "summary", firmId, params]`), which keeps
  // this from silently breaking on an unrelated codegen change.
  const invalidateSummary = () =>
    queryClient.invalidateQueries({
      predicate: (q) => {
        if (!Array.isArray(q.queryKey)) return false;
        const hasBinderVerification = q.queryKey.some(
          (k) => typeof k === "string" && k.includes("binder-verification"),
        );
        const hasSummary = q.queryKey.some(
          (k) => typeof k === "string" && k.includes("summary"),
        );
        return hasBinderVerification && hasSummary;
      },
    });

  const [pendingS3Key, setPendingS3Key] = useState<string | null>(null);
  const [bulkConfirm, setBulkConfirm] = useState<
    | null
    | { verdict: "verified_present" | "not_required"; targetCount: number }
  >(null);
  const [bulkProgress, setBulkProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);

  const job = data as JobLike | undefined;
  const binder = pickBinder(job);
  const verification = binder?.verification;
  const artifactId = binder?.artifactId;
  const inFlight =
    verification?.pipelineStatus === "pending" || verification?.pipelineStatus === "processing";

  const onReverify = async () => {
    if (!jobId) return;
    await trigger.mutateAsync({ jobId });
    refetch();
  };

  const onSetVerdict = async (
    doc: MissingDoc,
    verdict: "verified_present" | "not_required",
  ) => {
    if (!jobId || !artifactId) return;
    setPendingS3Key(doc.s3Key);
    try {
      await setVerdict.mutateAsync({
        jobId,
        artifactId,
        s3KeyB64: encodeS3KeyForPath(doc.s3Key),
        data: { verdict },
      });
      await refetch();
      await invalidateSummary();
    } finally {
      setPendingS3Key(null);
    }
  };

  const runBulkVerdict = async (
    verdict: "verified_present" | "not_required",
  ) => {
    if (!jobId || !artifactId) return;
    const targets = (verification?.missingDocs ?? []).filter(
      (d) => !d.reviewerVerdict?.verdict,
    );
    if (targets.length === 0) return;

    setBulkProgress({ done: 0, total: targets.length });
    setBulkConfirm(null);

    // Sequential to keep DynamoDB writes ordered against the same job item and
    // avoid hammering API Gateway with N parallel writes. Typical N is small
    // (<30) — sequential cost is acceptable.
    let done = 0;
    for (const doc of targets) {
      try {
        await setVerdict.mutateAsync({
          jobId,
          artifactId,
          s3KeyB64: encodeS3KeyForPath(doc.s3Key),
          data: { verdict },
        });
      } catch {
        // Soft-fail: keep going so partial progress is preserved. The remaining
        // un-verdicted rows are still individually clickable after refetch.
      }
      done += 1;
      setBulkProgress({ done, total: targets.length });
    }

    await refetch();
    await invalidateSummary();
    setBulkProgress(null);
  };

  const onClearVerdict = async (doc: MissingDoc) => {
    if (!jobId || !artifactId) return;
    setPendingS3Key(doc.s3Key);
    try {
      await clearVerdict.mutateAsync({
        jobId,
        artifactId,
        s3KeyB64: encodeS3KeyForPath(doc.s3Key),
      });
      await refetch();
      await invalidateSummary();
    } finally {
      setPendingS3Key(null);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={!!jobId}
      onClose={onClose}
      PaperProps={{ sx: { width: 760 } }}
    >
      <Box p={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">{job?.clientName ?? "Binder verification"}</Typography>
          <IconButton onClick={onClose} aria-label="close"><CloseIcon /></IconButton>
        </Stack>
        {isLoading && <CircularProgress />}
        {isError && <Alert severity="error">Failed to load binder details.</Alert>}
        {job && (
          <>
            <Stack direction="row" spacing={2} alignItems="center" mb={2} flexWrap="wrap">
              <Chip size="small" label={binder?.fileName ?? "(no binder)"} />
              {verification?.partiallyScanned && (
                <Chip size="small" color="info" label="Partially scanned" />
              )}
              {(() => {
                // Mirrors the server-side `reviewer_complete` filter: every
                // originally-missing row now carries a reviewerVerdict.
                const docs = verification?.missingDocs ?? [];
                const reviewerResolved =
                  verification?.status === "issues_found" &&
                  docs.length > 0 &&
                  docs.every((d) => !!d.reviewerVerdict?.verdict);
                return reviewerResolved ? (
                  <Chip
                    size="small"
                    color="success"
                    variant="outlined"
                    label="Resolved by reviewer"
                  />
                ) : null;
              })()}
              <Button
                variant="contained"
                size="small"
                onClick={onReverify}
                disabled={inFlight || trigger.isPending}
              >
                {inFlight ? "Verifying…" : "Re-verify"}
              </Button>
            </Stack>
            {verification?.missingDocs && verification.missingDocs.length > 0 ? (
              <>
                {(() => {
                  const unresolvedCount = (verification.missingDocs ?? []).filter(
                    (d) => !d.reviewerVerdict?.verdict,
                  ).length;
                  const bulkBusy = bulkProgress !== null;
                  return (
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                      flexWrap="wrap"
                      rowGap={1}
                    >
                      <Typography variant="subtitle1">
                        Missing files ({verification.missingDocs.length})
                        {unresolvedCount !== verification.missingDocs.length && (
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            ml={1}
                          >
                            {unresolvedCount} unresolved
                          </Typography>
                        )}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        {bulkBusy && (
                          <Chip
                            size="small"
                            icon={<CircularProgress size={12} />}
                            label={`Marking ${bulkProgress.done}/${bulkProgress.total}…`}
                          />
                        )}
                        <Tooltip title="Mark every unresolved row as 'present in binder'">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              startIcon={<DoneAllIcon />}
                              disabled={bulkBusy || unresolvedCount === 0 || !artifactId}
                              onClick={() =>
                                setBulkConfirm({
                                  verdict: "verified_present",
                                  targetCount: unresolvedCount,
                                })
                              }
                            >
                              Mark all present
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title="Mark every unresolved row as 'not required for this binder'">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<BlockOutlinedIcon />}
                              disabled={bulkBusy || unresolvedCount === 0 || !artifactId}
                              onClick={() =>
                                setBulkConfirm({
                                  verdict: "not_required",
                                  targetCount: unresolvedCount,
                                })
                              }
                            >
                              Mark all not required
                            </Button>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  );
                })()}
                <Table size="small" sx={{ tableLayout: "fixed" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>File</TableCell>
                      <TableCell sx={{ width: 100 }}>Doc type</TableCell>
                      <TableCell sx={{ width: 110 }}>Issuer</TableCell>
                      <TableCell sx={{ width: 90 }}>Account</TableCell>
                      <TableCell align="right" sx={{ width: 130 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {verification.missingDocs.map((d) => {
                      const busy = pendingS3Key === d.s3Key;
                      const v = d.reviewerVerdict;
                      return (
                        <TableRow key={d.s3Key ?? d.fileName}>
                          <TableCell sx={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                            {d.fileName}
                          </TableCell>
                          <TableCell sx={{ wordBreak: "break-word" }}>{d.documentType ?? "—"}</TableCell>
                          <TableCell sx={{ wordBreak: "break-word" }}>{d.issuer ?? "—"}</TableCell>
                          <TableCell sx={{ wordBreak: "break-word" }}>{d.accountNumber ?? "—"}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                            {v?.verdict ? (
                              <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                                <VerdictChip verdict={v} />
                                <Tooltip title="Undo verdict">
                                  <span>
                                    <IconButton
                                      size="small"
                                      aria-label="Undo verdict"
                                      onClick={() => onClearVerdict(d)}
                                      disabled={busy || !artifactId}
                                    >
                                      {busy ? <CircularProgress size={14} /> : <UndoIcon fontSize="small" />}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Stack>
                            ) : (
                              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                <Tooltip title="Mark present — the file IS in the binder">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="success"
                                      aria-label="Mark present"
                                      disabled={busy || !artifactId}
                                      onClick={() => onSetVerdict(d, "verified_present")}
                                    >
                                      {busy ? <CircularProgress size={14} /> : <CheckCircleOutlineIcon fontSize="small" />}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Not required — this file isn't needed for this binder">
                                  <span>
                                    <IconButton
                                      size="small"
                                      aria-label="Not required"
                                      disabled={busy || !artifactId}
                                      onClick={() => onSetVerdict(d, "not_required")}
                                    >
                                      {busy ? <CircularProgress size={14} /> : <BlockOutlinedIcon fontSize="small" />}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Stack>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </>
            ) : verification?.status === "verified" ? (
              <Alert severity="success">All expected docs found in binder.</Alert>
            ) : (
              <Typography color="text.secondary">No verification has run yet.</Typography>
            )}
          </>
        )}
      </Box>
      <Dialog
        open={!!bulkConfirm}
        onClose={() => bulkProgress === null && setBulkConfirm(null)}
        maxWidth="xs"
      >
        <DialogTitle>
          {bulkConfirm?.verdict === "verified_present"
            ? "Mark all present?"
            : "Mark all not required?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {bulkConfirm?.verdict === "verified_present"
              ? `This will record that all ${bulkConfirm?.targetCount} unresolved files are already present in the binder.`
              : `This will record that all ${bulkConfirm?.targetCount} unresolved files are not required for this binder.`}{" "}
            Each row can still be undone individually.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={bulkConfirm?.verdict === "verified_present" ? "success" : "primary"}
            onClick={() => bulkConfirm && runBulkVerdict(bulkConfirm.verdict)}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
}
